import { Link } from "@/lib/router-compat";
import { useTranslation } from "react-i18next";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { useLanguageRouting, getBasePath } from "@/hooks/useLanguageRouting";

export default function Breadcrumbs() {
  const { t } = useTranslation('common');
  const { getLocalizedLink, basePath } = useLanguageRouting();
  
  // Use base path (without language prefix) for breadcrumb logic
  const pathnames = basePath.split("/").filter((x) => x);

  // Don't show breadcrumbs on homepage
  if (basePath === "/") {
    return null;
  }

  const getLabel = (path: string) => {
    const routeMap: Record<string, string> = {
      "/": "breadcrumbs.home",
      "/what-is-nearshoring": "breadcrumbs.whatIsNearshoring",
      "/nearshoring-vs-offshoring-vs-outsourcing": "breadcrumbs.nearshoringVsOffshoring",
      "/companies": "breadcrumbs.companies",
      "/blog": "breadcrumbs.blog",
      "/privacy-policy": "breadcrumbs.privacy",
      "/imprint": "breadcrumbs.imprint",
    };

    return routeMap[path] ? t(routeMap[path]) : pathnames[pathnames.length - 1];
  };

  return (
    <nav aria-label="Breadcrumb" className="w-full pt-24 pb-4">
      <div className="container mx-auto px-4">
        <Breadcrumb>
          <BreadcrumbList className="flex-wrap gap-1 sm:gap-1.5 text-xs">
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link
                  to={getLocalizedLink("/")}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t('breadcrumbs.home')}
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>

            {pathnames.map((_, index) => {
              const path = `/${pathnames.slice(0, index + 1).join("/")}`;
              const isLast = index === pathnames.length - 1;
              const label = getLabel(path);

              return (
                <div key={path} className="inline-flex items-center gap-1 sm:gap-1.5">
                  <BreadcrumbSeparator className="text-muted-foreground/40 [&>svg]:size-3" />
                  <BreadcrumbItem>
                    {isLast ? (
                      <BreadcrumbPage className="font-medium text-foreground max-w-[200px] sm:max-w-none truncate">
                        {label}
                      </BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink asChild>
                        <Link
                          to={getLocalizedLink(path)}
                          className="text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {label}
                        </Link>
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                </div>
              );
            })}
          </BreadcrumbList>
        </Breadcrumb>
      </div>
    </nav>
  );
}
